import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../../components/layout/PageHeader';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { CenteredSpinner } from '../../../components/ui/Spinner';
import type { DateRange } from '../../../lib/reportingDateRange';
import { getSocialOverview, listSocialReportingAccounts } from './api';
import { SocialAccountPicker } from './SocialAccountPicker';
import { SocialPeriodSelector } from './SocialPeriodSelector';
import { AsOfLine } from './AsOfLine';
import { SocialOverviewTab } from './SocialOverviewTab';
import { SocialContentTab } from './SocialContentTab';
import { SocialGoogleBusinessTab } from './SocialGoogleBusinessTab';
import { SocialCommunityTab } from './SocialCommunityTab';
import {
  comparisonLabel,
  DEFAULT_SOCIAL_PERIOD,
  socialPeriodRange,
  socialPeriodRanges,
  type SocialPeriodPreset,
} from './socialPeriod';

// Organic Social Reporting — per-client page (Insights). Rounds out the Organic
// Social trio: inbox (incoming) → publishing (outgoing) → reporting (measurement).
// One account at a time (a client picker + a period selector), the same scoping
// posture as the publishing calendar: live Zernio reads, React-Query cached per
// account with a long staleTime + a manual refresh. Owns no Firestore collection.
//
// PASS 1: the shell + the Overview tab. The other three tabs are stubs, filled in
// later passes (Content, Audience & Growth, Community & Reputation).

// Long staleTime — social insights move slowly (Meta lag ≤24h) and each overview
// is several Zernio round-trips. Mirrors the calendar's external-posts posture.
const OVERVIEW_STALE_MS = 6 * 60 * 60_000;

type TabKey = 'overview' | 'content' | 'google-business' | 'community';

export function SocialReportingPage() {
  const qc = useQueryClient();
  const accounts = useQuery({
    queryKey: ['social-reporting-accounts'],
    queryFn: listSocialReportingAccounts,
  });

  const [accountId, setAccountId] = useState('');
  const [preset, setPreset] = useState<SocialPeriodPreset>(DEFAULT_SOCIAL_PERIOD);
  const [custom, setCustom] = useState<DateRange>(() =>
    socialPeriodRange(DEFAULT_SOCIAL_PERIOD, new Date()),
  );

  // Range + its comparable previous window, derived from one `now` so they can't
  // straddle a midnight boundary between two separate reads.
  const { range, previous: prev } = useMemo(
    () => socialPeriodRanges(preset, new Date(), custom),
    [preset, custom],
  );

  const overview = useQuery({
    queryKey: ['social-reporting-overview', accountId, range.from, range.to],
    queryFn: () => getSocialOverview(accountId, range),
    enabled: accountId !== '',
    staleTime: OVERVIEW_STALE_MS,
  });

  const refresh = () =>
    qc.invalidateQueries({
      queryKey: ['social-reporting-overview', accountId, range.from, range.to],
    });

  return (
    <div>
      <PageHeader
        title="Social reporting"
        description="Per-client organic social performance — reach, engagement, audience growth and the community you manage."
      />

      {/* Controls: client picker + period selector. */}
      <div className="mb-5 grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,24rem)_1fr]">
        <div>
          <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500">
            Client
          </label>
          {accounts.isLoading ? (
            <div className="rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-400">
              Loading clients…
            </div>
          ) : (
            <SocialAccountPicker
              accounts={accounts.data ?? []}
              value={accountId}
              onChange={setAccountId}
            />
          )}
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500">
            Period
          </label>
          <SocialPeriodSelector
            preset={preset}
            custom={custom}
            onPresetChange={setPreset}
            onCustomChange={setCustom}
          />
          <p className="mt-1 text-xs text-slate-400">{comparisonLabel(prev)}</p>
        </div>
      </div>

      {accountId === '' ? (
        <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-6 py-16 text-center text-sm text-slate-400">
          Pick a client to see their organic social report.
        </div>
      ) : (
        <>
          <div className="mb-3 flex items-center justify-between gap-3">
            <div>
              {overview.data && (
                <h2 className="text-lg font-semibold text-slate-900">{overview.data.accountName}</h2>
              )}
            </div>
            <AsOfLine
              lastSync={overview.data?.lastSync ?? null}
              syncing={overview.isFetching}
              onRefresh={refresh}
            />
          </div>

          <Tabs defaultValue={'overview' satisfies TabKey}>
            <TabsList>
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="content">Content</TabsTrigger>
              <TabsTrigger value="google-business">Google Business</TabsTrigger>
              <TabsTrigger value="community">Community &amp; Reputation</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="mt-4">
              {overview.isLoading ? (
                <div className="py-16">
                  <CenteredSpinner />
                </div>
              ) : overview.isError ? (
                <ErrorPanel onRetry={() => overview.refetch()} />
              ) : overview.data ? (
                <SocialOverviewTab data={overview.data} />
              ) : null}
            </TabsContent>

            <TabsContent value="content" className="mt-4">
              <SocialContentTab accountId={accountId} range={range} />
            </TabsContent>
            <TabsContent value="google-business" className="mt-4">
              <SocialGoogleBusinessTab accountId={accountId} range={range} />
            </TabsContent>
            <TabsContent value="community" className="mt-4">
              <SocialCommunityTab accountId={accountId} range={range} />
            </TabsContent>
          </Tabs>
        </>
      )}
    </div>
  );
}

function ErrorPanel({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="rounded-lg border border-red-200 bg-red-50 px-6 py-10 text-center">
      <p className="text-sm text-red-700">Couldn't load this client's report.</p>
      <button
        type="button"
        onClick={onRetry}
        className="mt-3 rounded-md border border-red-200 bg-white px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-50"
      >
        Try again
      </button>
    </div>
  );
}
